/**
 * MarkdownEditor — editor simples e robusto de conteúdo de artigo (admin).
 *
 * O conteúdo é MARKDOWN (## H2 / ### H3 alimentam o índice lateral automático).
 * Toolbar: H2, H3, negrito, itálico, lista, lista numerada, link e IMAGEM
 * (upload pro R2 via /api/help/upload — mesmo mecanismo do CRUD). Sem libs de
 * browser storage; opera direto no <textarea> manipulando a seleção.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heading2, Heading3, Bold, Italic, List, ListOrdered, Link2, Image as ImageIcon, Loader2,
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MarkdownEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const apply = (fn) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const v = value || '';
    const { text, selStart, selEnd } = fn(v, start, end);
    onChange(text);
    // Restaura o cursor após o React re-renderizar.
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(selStart, selEnd);
    });
  };

  const wrap = (before, after) => apply((v, s, e) => {
    const sel = v.slice(s, e) || '';
    const text = v.slice(0, s) + before + sel + after + v.slice(e);
    return { text, selStart: s + before.length, selEnd: s + before.length + sel.length };
  });

  const linePrefix = (prefix) => apply((v, s) => {
    // Acha o início da linha onde está o cursor.
    const lineStart = v.lastIndexOf('\n', s - 1) + 1;
    const text = v.slice(0, lineStart) + prefix + v.slice(lineStart);
    return { text, selStart: s + prefix.length, selEnd: s + prefix.length };
  });

  const insertLink = () => apply((v, s, e) => {
    const sel = v.slice(s, e) || 'texto';
    const snippet = `[${sel}](https://)`;
    const text = v.slice(0, s) + snippet + v.slice(e);
    // posiciona o cursor dentro do (https://)
    const urlPos = s + sel.length + 3;
    return { text, selStart: urlPos, selEnd: urlPos + 8 };
  });

  const insertImageMarkdown = (url, alt) => apply((v, s) => {
    const snippet = `\n![${alt || 'imagem'}](${url})\n`;
    const text = v.slice(0, s) + snippet + v.slice(s);
    const pos = s + snippet.length;
    return { text, selStart: pos, selEnd: pos };
  });

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/help/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      insertImageMarkdown(res.data.url, file.name?.replace(/\.[^.]+$/, ''));
      toast.success('Imagem inserida!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no upload da imagem');
    } finally {
      setUploading(false);
    }
  };

  const Btn = ({ title, onClick, children }) => (
    <button type="button" title={title} onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '30px',
      borderRadius: '7px', border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-card)',
      color: 'var(--dp-text-secondary)', cursor: 'pointer',
    }}>{children}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <Btn title="Título de seção (H2)" onClick={() => linePrefix('## ')}><Heading2 size={15} /></Btn>
        <Btn title="Subtítulo (H3)" onClick={() => linePrefix('### ')}><Heading3 size={15} /></Btn>
        <Btn title="Negrito" onClick={() => wrap('**', '**')}><Bold size={15} /></Btn>
        <Btn title="Itálico" onClick={() => wrap('*', '*')}><Italic size={15} /></Btn>
        <Btn title="Lista" onClick={() => linePrefix('- ')}><List size={15} /></Btn>
        <Btn title="Lista numerada" onClick={() => linePrefix('1. ')}><ListOrdered size={15} /></Btn>
        <Btn title="Link" onClick={insertLink}><Link2 size={15} /></Btn>
        <label title="Inserir imagem" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '30px',
          borderRadius: '7px', border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-card)',
          color: 'var(--dp-text-secondary)', cursor: uploading ? 'default' : 'pointer',
        }}>
          {uploading ? <Loader2 size={15} className="dp-spin" /> : <ImageIcon size={15} />}
          <input type="file" accept="image/*" hidden disabled={uploading}
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
      </div>
      <textarea
        ref={ref}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Escreva em Markdown. Use ## para títulos de seção, ### para subtítulos.'}
        style={{
          width: '100%', boxSizing: 'border-box', minHeight: '240px', resize: 'vertical',
          padding: '12px', borderRadius: '10px', border: '1px solid var(--dp-border-subtle)',
          background: 'var(--dp-bg-input)', color: 'var(--dp-text-primary)',
          fontSize: '13.5px', lineHeight: 1.6, fontFamily: 'var(--dp-font)', outline: 'none',
        }}
      />
      <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', marginTop: '6px' }}>
        Dica: <b>##</b> vira título de seção e <b>###</b> subtítulo — eles montam o índice lateral do artigo.
      </div>
    </div>
  );
}
